// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './BaleCard.scss';

import { storage, firestore } from '../../context/firebase';
import { fetchImageThumbnailUrl } from '../../shared_components/utils/ImgUtils';
import { doc } from 'firebase/firestore';
import { AlertTriangle } from 'lucide-react';
import LoadingIcon from '../LoadingIcon/LoadingIcon';

import alert_on_icon from '../../assets/icons/red/alert.png';
import { BALE_DOCUMENT_PATH } from '../../shared_components/utils/FirebaseUtils';
import { useAuthStore } from '../../stores/authStore';
import { useGetDocument } from '../../shared_components/hooks/useGetDocument';

export default function BaleCard({
  imageStoragePath,
  selected = true,
  flagged = false,
  baleId = null,
  deliveryId = null,
  isLoading = false,
}) {
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const [baleImg, setBaleImg] = React.useState('');
  const [isLoaded, setIsLoaded] = React.useState(false);

  const hasRequiredValues = !!baleId && !!deliveryId && !!currentUserClientId;
  const baleDocRef = hasRequiredValues
    ? doc(firestore, BALE_DOCUMENT_PATH(currentUserClientId, deliveryId, baleId))
    : null;

  const { data: baleData } = useGetDocument(baleDocRef, {
    initialCall: hasRequiredValues,
    withSnapshot: true,
  });
  const imageIssueClass = baleData?.predicted_labels?.image_issues?.class;
  const hasImageIssues = imageIssueClass && imageIssueClass !== 'no_issues';

  React.useEffect(() => {
    if (!imageStoragePath) return;
    if (imageStoragePath.startsWith('data:image')) {
      // This means it is not actually an URL but the image data itself
      setBaleImg(imageStoragePath);
    } else {
      fetchImageThumbnailUrl(storage, imageStoragePath).then((url) => {
        setBaleImg(url);
      });
    }
  }, [imageStoragePath]);

  return (
    <div className='bale_card'>
      {baleImg && (
        <img
          className={`bale_card--img ${!selected ? 'deselected' : ''} ${hasImageIssues ? 'has-image-issues' : ''} ${isLoading ? 'loading' : ''}`}
          src={baleImg}
          onLoad={() => setIsLoaded(true)}
          style={{ display: isLoaded ? 'block' : 'none' }}
        />
      )}
      {flagged && <img className='bale_card--alert' src={alert_on_icon} />}
      {/* Only show warning for images that couldn't be analyzed */}
      {baleId && deliveryId && hasImageIssues && (
        <div className='bale_card--image-quality-warning'>
          <AlertTriangle size={16} />
        </div>
      )}
      {isLoading && (
        <div className='bale_card--loading'>
          <LoadingIcon />
        </div>
      )}
    </div>
  );
}
